import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { Archive, Visibility } from "@mui/icons-material";
import {
  getClientArchive,
  restoreClientArchiveToOrigin,
} from "../services/source";
import { openFileUrl } from "../services/fileUtils";

const FILE_BASE_URL = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

const ARQUIVO_TABS = [
  { key: "documentos", label: "Documentos" },
  { key: "contratos", label: "Contratos" },
  { key: "servicos", label: "Serviços" },
];

const DOCUMENT_CATEGORIES = new Set([
  "certidão",
  "publicação",
  "estatuto",
  "nif",
  "alvara",
  "documentos dos sócios",
  "documentos dos gerentes",
  "procurações",
]);

const CONTRACT_CATEGORIES = new Set([
  "copia do contrato assinado",
  "copia de adendas",
  "copia das faturas",
  "copia recibos",
  "relatorio do que falta facturar",
  "relatorio do que falta pagar",
  "relatorio do trabalho em falta",
]);

const normalize = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const resolveItemTab = (item) => {
  const originType = normalize(item?.origem_tipo);
  const originCategory = normalize(item?.origem_categoria || item?.categoria);
  const originTabName = normalize(item?.origem_tab_nome);

  if (
    ["documentos", "documento", "document", "documents"].includes(originType)
  ) {
    return "documentos";
  }

  if (["contratos", "contrato", "contract", "contracts"].includes(originType)) {
    return "contratos";
  }

  if (
    ["servicos", "serviços", "serviço", "service", "services"].includes(
      originType,
    )
  ) {
    return "servicos";
  }

  if (originTabName) {
    return "servicos";
  }

  if (DOCUMENT_CATEGORIES.has(originCategory)) {
    return "documentos";
  }

  if (CONTRACT_CATEGORIES.has(originCategory)) {
    return "contratos";
  }

  if (
    originCategory.includes("contrato") ||
    originCategory.includes("fatura") ||
    originCategory.includes("recibo") ||
    originCategory.includes("relatorio")
  ) {
    return "contratos";
  }

  if (
    originCategory.includes("certidao") ||
    originCategory.includes("estatuto") ||
    originCategory.includes("procuracoes") ||
    originCategory.includes("documento")
  ) {
    return "documentos";
  }

  return "servicos";
};

const formatDate = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleDateString("pt-PT");
};

const Arquivo = ({ companyId, clientId }) => {
  const [selectedTab, setSelectedTab] = useState("documentos");
  const [loading, setLoading] = useState(false);
  const [restoringId, setRestoringId] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [items, setItems] = useState([]);
  const [openingId, setOpeningId] = useState(0);

  const loadArchive = useCallback(async () => {
    if (!companyId) {
      setItems([]);
      return;
    }

    try {
      setLoading(true);
      const res = await getClientArchive(companyId, clientId);
      setItems(res?.success ? res.data || [] : []);
    } catch (loadError) {
      console.error("Error loading archive:", loadError);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [companyId, clientId]);

  useEffect(() => {
    loadArchive();
  }, [loadArchive]);

  const filteredItems = useMemo(() => {
    return items
      .filter((item) => {
        const originClientId = String(item?.origem_cliente_id || "");
        const sameClient =
          originClientId === "" || originClientId === String(clientId || "");
        return sameClient && resolveItemTab(item) === selectedTab;
      })
      .sort((a, b) => Number(b?.id || 0) - Number(a?.id || 0));
  }, [items, clientId, selectedTab]);

  const handleOpen = async (file) => {
    if (!file?.caminho) return;
    setOpeningId(Number(file.id));

    const cleanPath = String(file.caminho).replace(/^\/+/, "");
    const fileUrl = `${FILE_BASE_URL}/${cleanPath}`;

    try {
      const ok = await openFileUrl(
        file.caminho,
        file.nome_original || file.nome_ficheiro,
      );
      if (!ok) window.open(fileUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      window.open(fileUrl, "_blank", "noopener,noreferrer");
    } finally {
      setOpeningId(0);
    }
  };

  const handleRestore = async (file) => {
    const archiveId = Number(file?.id || 0);
    if (!archiveId || !companyId) {
      return;
    }

    try {
      setRestoringId(archiveId);
      setError("");
      setSuccess("");

      const res = await restoreClientArchiveToOrigin({
        archive_id: archiveId,
        company_id: companyId,
      });

      if (!res?.success) {
        throw new Error(
          res?.message || "Não foi possível restaurar o ficheiro.",
        );
      }

      await loadArchive();
      setSuccess("Ficheiro restaurado com sucesso.");
    } catch (restoreError) {
      console.error("Restore error:", restoreError);
      setError(
        restoreError?.message || "Não foi possível restaurar o ficheiro.",
      );
    } finally {
      setRestoringId(0);
    }
  };

  return (
    <Card
      sx={{
        borderRadius: 4,
        border: "1px solid #e7e9f1",
        background:
          "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(248,249,253,1) 100%)",
      }}
    >
      <CardContent>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: 1,
            mb: 2,
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            Arquivo
          </Typography>
          <Chip
            label={`${filteredItems.length} arquivo(s)`}
            size="small"
            color="secondary"
            sx={{ fontWeight: 700 }}
          />
        </Box>

        <Tabs
          value={selectedTab}
          onChange={(_, value) => setSelectedTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            mb: 2,
            backgroundColor: "rgba(114, 4, 241, 0.18)",
            borderRadius: 2,
            "& .MuiTabs-indicator": { display: "none" },
          }}
        >
          {ARQUIVO_TABS.map((tab) => (
            <Tab
              key={tab.key}
              value={tab.key}
              label={tab.label}
              sx={{
                textTransform: "none",
                "&.Mui-selected": {
                  color: "#fff",
                  backgroundColor: "#9c27b0",
                },
              }}
            />
          ))}
        </Tabs>

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        {success ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        ) : null}

        {loading ? (
          <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={26} />
          </Box>
        ) : (
          <List dense>
            {filteredItems.map((file) => (
              <ListItem key={file.id} divider>
                <ListItemText
                  primary={
                    file.nome_original || file.nome_ficheiro || "Arquivo"
                  }
                  secondary={`${file.origem_categoria || "Sem categoria"} | ${formatDate(file.data_registo || file.created_at)}`}
                />
                <ListItemSecondaryAction>
                  <Tooltip title="Visualizar ficheiro">
                    <IconButton
                      color="warning"
                      onClick={() => handleOpen(file)}
                      disabled={openingId === file.id}
                    >
                      {openingId === file.id ? (
                        <CircularProgress size={18} />
                      ) : (
                        <Visibility />
                      )}
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Restaurar para origem">
                    <IconButton
                      color="success"
                      onClick={() => handleRestore(file)}
                      disabled={restoringId === Number(file.id)}
                    >
                      <Archive />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              </ListItem>
            ))}

            {!filteredItems.length ? (
              <ListItem>
                <ListItemText
                  primary="Sem arquivos nesta tab"
                  secondary="Arquive ficheiros em Documentos, Contratos ou Serviços."
                />
              </ListItem>
            ) : null}
          </List>
        )}
      </CardContent>
    </Card>
  );
};

export default Arquivo;
